/*
 * custom-contact-upload.js — file attachments for sections/custom-contact-form.liquid
 *
 * The contact form stays Shopify's own form. This script only adds a step in
 * front of its submit when files are attached:
 *
 *   1. the files go straight to our storage through PrinsteyUpload
 *      (assets/custom-store-upload.js, context "custom"), with progress;
 *   2. three hidden fields are filled —
 *        contact[Files]             "3 files · 45.2 MB: a.stl, b.jpg, c.zip"
 *        contact[File links]        one download link per line
 *        contact[Upload reference]  the upload's id
 *   3. the form is submitted natively, so Shopify's contact email, its spam
 *      check and its success message all work exactly as before.
 *
 * No files attached: nothing is intercepted. Upload fails: the customer can try
 * again or send the message without the files (contact[Files] then says so).
 *
 * The submit listener sits on window in the capture phase so it runs before any
 * other submit handler on the page (a captcha script, for one) and can hold the
 * submit back while the files upload. The later native submit passes through.
 * Shopify's hCaptcha script cancels that submit every time and posts the form
 * itself only once the check passes; if the check fails or the customer closes
 * it, nothing posts. So a send that is still on the page a few seconds later
 * goes back to a usable button, keeping the filled fields and the finished
 * upload (a late captcha post still carries them; a second press does not
 * upload again).
 *
 * Markup contract (all inside the section wrapper [data-ccf-uploads]):
 *   script[data-ccf-config]  JSON { endpoint, maxFiles, maxTotalBytes, maxTotalLabel, extensions, strings }
 *   form  [data-ccf-attach] [data-ccf-drop] [data-ccf-drop-title] [data-ccf-input]
 *         [data-ccf-msg] [data-ccf-list] [data-ccf-summary]
 *         input[data-ccf-field=files|links|reference]  [data-ccf-submit]
 *         [data-ccf-progress] [data-ccf-progress-bar] [data-ccf-progress-fill]
 *         [data-ccf-progress-detail] [data-ccf-cancel]
 *         [data-ccf-failed] [data-ccf-failed-text] [data-ccf-retry] [data-ccf-skip]
 *
 * Test hook: window.PrinsteyContact.instances[n].addFiles(File[]) / removeFile(i)
 * / getState(). Plain ES2017, no dependencies, safe to load more than once.
 */
(function () {
  'use strict';

  if (window.PrinsteyContact && window.PrinsteyContact.version) {
    window.PrinsteyContact.init(document);
    return;
  }

  const VERSION = '1.1.0';
  const CONCURRENCY = 4;
  /* How long "Sending…" holds after the send: when another script held the
     submit back (a captcha deciding), and when nothing did but the page has
     still not moved on. */
  const SEND_HELD_MS = 5000;
  const SEND_STUCK_MS = 20000;
  /* The service's own fallback sentence is shown for these; everything else
     gets the section's upload_failed_text. */
  const SERVER_SENTENCE = { rate_limited: 1, banned: 1, too_large: 1, type: 1, bad_request: 1 };

  const instances = [];

  /* --------------------------------------------------------------- helpers */

  function fill(template, values) {
    return String(template == null ? '' : template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) && values[key] != null ? String(values[key]) : match;
    });
  }

  function lib() {
    return window.PrinsteyUpload && typeof window.PrinsteyUpload.upload === 'function' ? window.PrinsteyUpload : null;
  }

  function formatBytes(n) {
    const l = lib();
    if (l && typeof l.formatBytes === 'function') return l.formatBytes(n);
    const v = Number(n) || 0;
    if (v < 1024) return Math.round(v) + ' B';
    const units = ['KB', 'MB', 'GB'];
    let x = v / 1024;
    let i = 0;
    while (x >= 1024 && i < units.length - 1) { x /= 1024; i += 1; }
    return (x >= 100 ? String(Math.round(x)) : x.toFixed(1)) + ' ' + units[i];
  }

  function formatDuration(seconds) {
    const l = lib();
    if (l && typeof l.formatDuration === 'function') return l.formatDuration(seconds);
    return Math.max(1, Math.ceil(Number(seconds) || 0)) + ' s';
  }

  function extensionOf(name) {
    const match = /\.([^./\\]+)$/.exec(String(name || ''));
    return match ? match[1].toLowerCase() : '';
  }

  function fileKey(file) {
    return [file.name, file.size, file.lastModified || 0].join(':');
  }

  function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }

  function svgIcon(path) {
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 16 16');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const p = document.createElementNS(ns, 'path');
    p.setAttribute('d', path);
    svg.appendChild(p);
    return svg;
  }

  /* -------------------------------------------------------------- instance */

  class ContactUpload {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('form');
      let cfg = {};
      try {
        const node = root.querySelector('script[data-ccf-config]');
        cfg = node ? JSON.parse(node.textContent) : {};
      } catch (e) {
        cfg = {};
      }
      this.cfg = cfg;
      this.strings = cfg.strings || {};
      this.maxFiles = Math.max(1, parseInt(cfg.maxFiles, 10) || 20);
      this.maxTotalBytes = Number(cfg.maxTotalBytes) || 2000 * 1048576;
      this.maxTotalLabel = cfg.maxTotalLabel || formatBytes(this.maxTotalBytes);
      this.extensions = (Array.isArray(cfg.extensions) ? cfg.extensions : [])
        .map(function (e) { return String(e || '').trim().replace(/^\./, '').toLowerCase(); })
        .filter(Boolean);

      const q = (sel) => this.form.querySelector(sel);
      this.els = {
        attach: q('[data-ccf-attach]'),
        drop: q('[data-ccf-drop]'),
        dropTitle: q('[data-ccf-drop-title]'),
        input: q('[data-ccf-input]'),
        msg: q('[data-ccf-msg]'),
        list: q('[data-ccf-list]'),
        summary: q('[data-ccf-summary]'),
        filesField: q('[data-ccf-field="files"]'),
        linksField: q('[data-ccf-field="links"]'),
        referenceField: q('[data-ccf-field="reference"]'),
        submit: q('[data-ccf-submit]') || q('[type="submit"]'),
        progress: q('[data-ccf-progress]'),
        bar: q('[data-ccf-progress-bar]'),
        fill: q('[data-ccf-progress-fill]'),
        detail: q('[data-ccf-progress-detail]'),
        cancel: q('[data-ccf-cancel]'),
        failed: q('[data-ccf-failed]'),
        failedText: q('[data-ccf-failed-text]'),
        retry: q('[data-ccf-retry]'),
        skip: q('[data-ccf-skip]')
      };
      this.submitLabel = this.els.submit ? this.els.submit.textContent : '';

      this.files = [];            // { id, file, key }
      this.rows = new Map();      // id -> { li, status, remove }
      this.listKey = null;
      this.nextId = 1;
      this.phase = 'idle';        // idle | uploading | sending | failed
      this.error = null;          // { message, code }
      this.messages = [];
      this.progress = null;
      this.cached = null;         // { key, result } of the last finished upload
      this.controller = null;
      this.seq = 0;
      this.sawSubmit = false;
      this.returnPhase = 'idle';  // where a send that did not leave the page goes back to
      this.sendTimer = null;
      this.sendHeld = false;      // the last send was held back by another submit listener
      this.changedKey = null;     // key of the file that changed on disk, for its row status
      this.dragDepth = 0;
      this.lastSubmission = null; // what the hidden fields were filled with, for tests

      this.bind();
      if (this.els.attach) this.els.attach.hidden = false;
      this.render();
    }

    /* ---------------------------------------------------------- events */

    bind() {
      const els = this.els;
      const on = (target, type, fn, opts) => {
        if (!target) return;
        target.addEventListener(type, fn, opts);
        this.listeners.push([target, type, fn, opts]);
      };
      this.listeners = [];

      on(window, 'submit', (e) => this.onSubmit(e), true);
      on(window, 'pageshow', (e) => { if (e.persisted) this.reset(); });

      const isSpace = (e) => e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space';
      on(els.drop, 'click', () => this.openPicker());
      on(els.drop, 'keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.openPicker();
        } else if (isSpace(e)) {
          e.preventDefault(); // no page scroll; activate on keyup like a real button
          this.spaceDown = true;
        }
      });
      on(els.drop, 'keyup', (e) => {
        if (isSpace(e) && this.spaceDown) {
          e.preventDefault();
          this.spaceDown = false;
          this.openPicker();
        }
      });
      on(els.drop, 'blur', () => { this.spaceDown = false; });

      on(els.input, 'change', () => {
        const picked = els.input.files ? Array.prototype.slice.call(els.input.files) : [];
        els.input.value = ''; // the same file can be chosen again after removing it
        if (picked.length) this.addFiles(picked);
      });

      /* Dropping anywhere on the section adds the files — and stops the browser
         from opening a file that misses the zone, which would lose what the
         customer has typed. */
      const hasFiles = (e) => {
        const types = e.dataTransfer && e.dataTransfer.types;
        return !!types && Array.prototype.indexOf.call(types, 'Files') !== -1;
      };
      on(this.root, 'dragenter', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        this.dragDepth += 1;
        this.setDragging(true);
      });
      on(this.root, 'dragover', (e) => {
        if (!hasFiles(e)) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = this.busy() ? 'none' : 'copy';
      });
      on(this.root, 'dragleave', (e) => {
        if (!hasFiles(e)) return;
        this.dragDepth = Math.max(0, this.dragDepth - 1);
        if (this.dragDepth === 0) this.setDragging(false);
      });
      on(this.root, 'drop', (e) => {
        if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
        e.preventDefault();
        this.dragDepth = 0;
        this.setDragging(false);
        if (this.busy()) return;
        const files = Array.prototype.slice.call(e.dataTransfer.files);
        const folders = new Set();
        const items = e.dataTransfer.items ? Array.prototype.filter.call(e.dataTransfer.items, function (it) { return it.kind === 'file'; }) : [];
        if (items.length === files.length) {
          items.forEach(function (item, i) {
            try {
              const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
              if (entry && entry.isDirectory) folders.add(files[i]);
            } catch (err) { /* not supported: size and type checks still apply */ }
          });
        }
        this.addFiles(files, folders);
      });

      on(els.cancel, 'click', () => {
        if (this.phase === 'uploading' && this.controller) this.controller.abort();
      });
      on(els.retry, 'click', () => {
        if (this.phase !== 'failed') return;
        if (!this.form.reportValidity || this.form.reportValidity()) this.start();
      });
      on(els.skip, 'click', () => {
        if (this.phase !== 'failed') return;
        if (this.form.reportValidity && !this.form.reportValidity()) return;
        this.sendWithoutFiles();
      });
    }

    destroy() {
      if (this.phase === 'uploading' && this.controller) this.controller.abort();
      this.seq += 1;
      clearTimeout(this.sendTimer);
      this.sendTimer = null;
      (this.listeners || []).forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
      this.listeners = [];
      const i = instances.indexOf(this);
      if (i !== -1) instances.splice(i, 1);
      if (this.root) delete this.root.__ccfUpload;
    }

    busy() {
      return this.phase === 'uploading' || this.phase === 'sending';
    }

    openPicker() {
      if (this.busy() || !this.els.input) return;
      this.els.input.click();
    }

    setDragging(on) {
      const drop = this.els.drop;
      if (!drop) return;
      const active = on && !this.busy();
      drop.classList.toggle('is-dragover', active);
      if (this.els.dropTitle) {
        const title = active && this.strings.dropActiveTitle ? this.strings.dropActiveTitle : this.strings.dropTitle;
        if (title && this.els.dropTitle.textContent !== title) this.els.dropTitle.textContent = title;
      }
    }

    /* ------------------------------------------------------------ files */

    totalBytes() {
      return this.files.reduce(function (sum, f) { return sum + f.file.size; }, 0);
    }

    signature() {
      return this.files.map(function (f) { return f.key; }).join('|');
    }

    addFiles(list, folders) {
      if (this.busy()) return false;
      const s = this.strings;
      const incoming = Array.prototype.slice.call(list || []).filter(function (f) {
        return f && typeof f.size === 'number' && typeof f.name === 'string';
      });
      const before = this.signature();
      const messages = [];
      const leftOut = [];
      const keys = new Set(this.files.map(function (f) { return f.key; }));
      let total = this.totalBytes();
      const values = { max_files: this.maxFiles, max_total: this.maxTotalLabel };

      incoming.forEach((file) => {
        const name = file.name || 'file';
        const named = Object.assign({ name: name }, values);
        if (folders && folders.has(file)) {
          messages.push(fill(s.folder, named));
          return;
        }
        if (this.extensions.length && this.extensions.indexOf(extensionOf(name)) === -1) {
          messages.push(fill(s.badType, named));
          return;
        }
        if (file.size < 1) {
          messages.push(fill(s.empty, named));
          return;
        }
        const key = fileKey(file);
        if (keys.has(key)) return; // already in the list
        if (this.files.length >= this.maxFiles) {
          leftOut.push(name);
          return;
        }
        if (total + file.size > this.maxTotalBytes) {
          messages.push(fill(s.tooBig, named));
          return;
        }
        keys.add(key);
        total += file.size;
        this.files.push({ id: this.nextId++, file: file, key: key });
      });
      if (leftOut.length) {
        messages.push(fill(s.tooMany, Object.assign({ names: leftOut.join(', '), count: leftOut.length }, values)));
      }
      this.messages = messages;
      if (this.signature() !== before) this.filesChanged();
      this.render();
      return true;
    }

    removeFile(index) {
      if (this.busy()) return false;
      if (index < 0 || index >= this.files.length) return false;
      const hadFocus = this.els.list && this.els.list.contains(document.activeElement);
      this.files.splice(index, 1);
      this.messages = [];
      this.filesChanged();
      this.render();
      if (hadFocus) {
        const next = this.files[index] || this.files[index - 1];
        const row = next ? this.rows.get(next.id) : null;
        if (row && row.remove) row.remove.focus();
        else if (this.els.drop) this.els.drop.focus();
      }
      return true;
    }

    /* A different file set: a finished upload for the old set is no longer
       what will be sent, so let the service throw it away, and clear any
       failure that belonged to the old set. */
    filesChanged() {
      if (this.cached && this.cached.key !== this.signature()) {
        const l = lib();
        if (l && typeof l.abortBeacon === 'function' && this.cached.result && this.cached.result.submissionId) {
          l.abortBeacon(this.cfg.endpoint, this.cached.result.submissionId);
        }
        this.cached = null;
      }
      if (this.phase === 'failed') {
        this.phase = 'idle';
        this.error = null;
      }
      this.changedKey = null;
      this.progress = null;
      this.clearFields();
    }

    /* ----------------------------------------------------------- submit */

    onSubmit(event) {
      if (event.target !== this.form) return;
      if (this.phase === 'sending') {
        // Our own native submit (or a script re-submitting it): let it through,
        // and notice if a later listener holds it back.
        this.sawSubmit = true;
        this.watchSend(event);
        return;
      }
      if (this.phase === 'uploading') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (!this.files.length) {
        this.clearFields(); // plain message, posted exactly as before
        return;
      }
      if (this.cached && this.cached.key === this.signature()) {
        // Already uploaded (the first send stopped on a validation error): no second upload.
        this.fillSuccess(this.cached.result);
        this.phase = 'sending';
        this.returnPhase = 'idle';
        this.sawSubmit = true;
        this.render();
        this.watchSend(event);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.start();
    }

    contact() {
      const value = (name) => {
        const el = this.form.querySelector('[name="' + name + '"]');
        return el && typeof el.value === 'string' ? el.value.trim() : '';
      };
      const contact = { name: value('contact[name]'), email: value('contact[email]') };
      const phone = value('contact[phone]');
      if (phone) contact.phone = phone;
      return { contact: contact, message: value('contact[body]') };
    }

    start() {
      if (this.busy() || !this.files.length) return;
      const seq = ++this.seq;
      const upload = lib();
      this.error = null;
      this.changedKey = null;
      this.messages = [];
      this.clearFields();
      if (!upload || !this.cfg.endpoint) {
        this.fail({ code: 'config' });
        return;
      }
      const key = this.signature();
      const files = this.files.map(function (f) { return f.file; });
      const who = this.contact();
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      this.controller = controller;
      this.phase = 'uploading';
      this.progress = { phase: 'init', loaded: 0, total: this.totalBytes(), fraction: 0, bytesPerSecond: 0, etaSeconds: null, filesDone: 0, filesTotal: files.length };
      // The button that started this is about to be disabled or hidden; keep
      // keyboard focus on the one control that still does something.
      const active = document.activeElement;
      const moveFocus = !!active && (active === this.els.submit || active === this.els.retry || active === this.els.skip);
      this.render();
      if (moveFocus && this.els.cancel) this.els.cancel.focus();

      upload.upload({
        endpoint: this.cfg.endpoint,
        context: 'custom',
        files: files,
        contact: who.contact,
        message: who.message,
        concurrency: CONCURRENCY,
        signal: controller ? controller.signal : undefined,
        onProgress: (p) => {
          if (seq !== this.seq || this.phase !== 'uploading' || !p) return;
          this.progress = p;
          this.renderProgress();
        }
      }).then((result) => {
        if (seq !== this.seq) return;
        this.controller = null;
        this.cached = { key: key, result: result };
        this.progress = Object.assign({}, this.progress || {}, { phase: 'done', fraction: 1, loaded: this.totalBytes(), total: this.totalBytes() });
        this.fillSuccess(result);
        this.submitNative('idle');
      }, (err) => {
        if (seq !== this.seq) return;
        this.controller = null;
        if (err && err.code === 'aborted') {
          this.phase = 'idle';
          this.progress = null;
          this.render();
          if (this.els.submit && (!document.activeElement || document.activeElement === document.body || document.activeElement === this.els.cancel)) {
            this.els.submit.focus();
          }
          return;
        }
        this.fail(err);
      });
    }

    fail(err) {
      const s = this.strings;
      const code = err && err.code ? String(err.code) : 'server';
      let message = SERVER_SENTENCE[code] && err && err.message ? err.message : s.failed;
      this.changedKey = null;
      if (code === 'file_changed') {
        // Saved over or moved on disk after it was added: every retry would
        // fail the same way, so name the file and mark its row.
        const entry = typeof err.fileIndex === 'number' ? this.files[err.fileIndex] : null;
        const name = err.fileName || (entry ? entry.file.name : '');
        if (entry) this.changedKey = entry.key;
        message = s.fileChanged ? fill(s.fileChanged, { name: name }) : (err.message || s.failed);
      }
      this.phase = 'failed';
      this.error = { message: message, code: code };
      this.progress = null;
      this.render();
      if (this.els.retry) this.els.retry.focus();
    }

    names() {
      return this.files.map(function (f) { return f.file.name; }).join(', ');
    }

    fillSuccess(result) {
      const count = this.files.length;
      const files = result && Array.isArray(result.files) ? result.files : [];
      const summary = count + (count === 1 ? ' file' : ' files') + ' · ' + formatBytes(this.totalBytes()) + ': ' + this.names();
      const links = files.map(function (f) { return f && f.link ? f.link : ''; }).filter(Boolean).join('\n');
      this.setField(this.els.filesField, summary);
      this.setField(this.els.linksField, links);
      this.setField(this.els.referenceField, result && result.submissionId ? String(result.submissionId) : '');
      this.lastSubmission = { files: summary, links: links, reference: result && result.submissionId || '' };
    }

    sendWithoutFiles() {
      const note = fill(this.strings.failedNote, {
        names: this.names(),
        count: this.files.length,
        size: formatBytes(this.totalBytes())
      });
      this.clearFields();
      this.setField(this.els.filesField, note);
      this.lastSubmission = { files: note, links: '', reference: '' };
      this.submitNative('failed');
    }

    /* Native submit, so Shopify's own flow runs. requestSubmit fires the submit
       event (a spam-check script sees it; our listener lets it pass because the
       phase is "sending") and re-checks the required fields. If that check
       stops it, go back to where we were: the upload is kept, not repeated. */
    submitNative(returnPhase) {
      const form = this.form;
      this.phase = 'sending';
      this.returnPhase = returnPhase === 'failed' ? 'failed' : 'idle';
      this.sawSubmit = false;
      this.render();
      if (typeof form.requestSubmit === 'function') {
        try {
          form.requestSubmit();
        } catch (e) {
          HTMLFormElement.prototype.submit.call(form);
          this.watchSend(null);
          return;
        }
        if (!this.sawSubmit) this.settleSend();
      } else {
        HTMLFormElement.prototype.submit.call(form);
        this.watchSend(null);
      }
    }

    /* A send leaves the page when it works. If it is still here after a few
       seconds — another listener cancelled the submit (Shopify's captcha does,
       and posts later only if its check passes) or the post never went — give
       the customer the button back. The hidden fields and the finished upload
       stay: a late post by that script still carries them, and pressing send
       again reuses the upload. */
    watchSend(event) {
      clearTimeout(this.sendTimer);
      const seq = this.seq;
      // A macrotask later every other submit listener has run.
      this.sendTimer = setTimeout(() => {
        this.sendTimer = null;
        if (seq !== this.seq || this.phase !== 'sending') return;
        this.sendHeld = !!event && event.defaultPrevented;
        this.sendTimer = setTimeout(() => {
          this.sendTimer = null;
          if (seq === this.seq) this.settleSend();
        }, this.sendHeld ? SEND_HELD_MS : SEND_STUCK_MS);
      }, 0);
    }

    settleSend() {
      clearTimeout(this.sendTimer);
      this.sendTimer = null;
      if (this.phase !== 'sending') return;
      this.phase = this.returnPhase === 'failed' ? 'failed' : 'idle';
      if (this.phase === 'idle') this.progress = null;
      this.render();
    }

    setField(el, value) {
      if (!el) return;
      el.value = value;
      el.disabled = !value;
    }

    clearFields() {
      [this.els.filesField, this.els.linksField, this.els.referenceField].forEach(function (el) {
        if (!el) return;
        el.value = '';
        el.disabled = true;
      });
    }

    /* Back/forward cache: the page comes back exactly as it was left, possibly
       mid-"Sending…". Start clean; the file list stays so it can be sent again. */
    reset() {
      this.seq += 1;
      clearTimeout(this.sendTimer);
      this.sendTimer = null;
      this.sendHeld = false;
      this.changedKey = null;
      this.controller = null;
      this.phase = 'idle';
      this.error = null;
      this.messages = [];
      this.progress = null;
      this.cached = null;
      this.sawSubmit = false;
      this.dragDepth = 0;
      this.setDragging(false);
      this.clearFields();
      this.render();
    }

    /* ----------------------------------------------------------- render */

    render() {
      const els = this.els;
      const s = this.strings;
      const busy = this.busy();

      if (els.drop) {
        els.drop.setAttribute('aria-disabled', busy ? 'true' : 'false');
        if (busy) els.drop.classList.remove('is-dragover');
      }

      this.renderList();

      if (els.msg) {
        els.msg.textContent = '';
        this.messages.forEach(function (text) {
          const p = document.createElement('p');
          p.textContent = text;
          els.msg.appendChild(p);
        });
        els.msg.hidden = this.messages.length === 0;
      }

      if (els.summary) {
        if (this.files.length) {
          const text = fill(s.summary, {
            count: this.files.length,
            size: formatBytes(this.totalBytes()),
            max_files: this.maxFiles,
            max_total: this.maxTotalLabel
          });
          if (els.summary.textContent !== text) els.summary.textContent = text;
          els.summary.hidden = !text;
        } else {
          els.summary.textContent = '';
          els.summary.hidden = true;
        }
      }

      if (els.submit) {
        els.submit.hidden = this.phase === 'failed';
        els.submit.disabled = busy;
        if (busy) els.submit.setAttribute('aria-busy', 'true');
        else els.submit.removeAttribute('aria-busy');
        if (this.phase === 'sending') this.setButtonText(s.sending || this.submitLabel);
        else if (this.phase !== 'uploading') this.setButtonText(this.submitLabel);
      }

      if (els.failed) {
        els.failed.hidden = this.phase !== 'failed';
        if (els.failedText) els.failedText.textContent = this.phase === 'failed' && this.error ? this.error.message : '';
      }

      if (els.progress) {
        const showProgress = this.phase === 'uploading' || (this.phase === 'sending' && !!this.progress);
        els.progress.hidden = !showProgress;
        if (els.cancel) els.cancel.hidden = this.phase !== 'uploading';
      }
      this.renderProgress();
    }

    setButtonText(text) {
      if (this.els.submit && this.els.submit.textContent !== text) this.els.submit.textContent = text;
    }

    renderList() {
      const els = this.els;
      if (!els.list) return;
      const key = this.files.map(function (f) { return f.id; }).join(',');
      if (key !== this.listKey) {
        this.listKey = key;
        this.rows.clear();
        els.list.textContent = '';
        this.files.forEach((entry, index) => {
          const li = document.createElement('li');
          li.className = 'ccf__file';
          const main = document.createElement('span');
          main.className = 'ccf__file-main';
          const name = document.createElement('span');
          name.className = 'ccf__file-name';
          name.textContent = entry.file.name;
          name.title = entry.file.name;
          const size = document.createElement('span');
          size.className = 'ccf__file-size';
          size.textContent = formatBytes(entry.file.size);
          const status = document.createElement('span');
          status.className = 'ccf__file-status';
          main.appendChild(name);
          main.appendChild(size);
          main.appendChild(status);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'ccf__file-remove';
          remove.setAttribute('aria-label', (this.strings.remove || 'Remove') + ' ' + entry.file.name);
          remove.appendChild(svgIcon('M4 4l8 8M12 4l-8 8'));
          remove.addEventListener('click', () => {
            const i = this.files.indexOf(entry);
            if (i !== -1) this.removeFile(i);
          });
          li.appendChild(main);
          li.appendChild(remove);
          els.list.appendChild(li);
          this.rows.set(entry.id, { li: li, status: status, remove: remove, index: index });
        });
      }
      els.list.hidden = this.files.length === 0;
      const busy = this.busy();
      this.rows.forEach(function (row) { row.remove.disabled = busy; });
      this.renderStatuses();
    }

    /* Per-file status. The library reports one running total, and it sends the
       parts of each file in list order, so the bytes are shared out in that
       order — close enough for a status line, and every file ends "Received". */
    renderStatuses() {
      const s = this.strings;
      const p = this.progress;
      let offset = 0;
      const loaded = p ? Number(p.loaded) || 0 : 0;
      const received = !!this.cached && this.cached.key === this.signature();
      this.files.forEach((entry) => {
        const row = this.rows.get(entry.id);
        if (!row) return;
        const size = entry.file.size;
        let text = s.statusReady || '';
        let state = '';
        if (this.phase === 'failed') {
          text = this.changedKey && entry.key === this.changedKey && s.statusChanged ? s.statusChanged : s.statusFailed;
          state = 'failed';
        } else if ((this.phase === 'sending' && p) || (this.phase === 'idle' && received)) {
          text = s.statusDone;
          state = 'done';
        } else if (this.phase === 'uploading' && p) {
          if (p.phase === 'completing' || p.phase === 'done') {
            text = s.statusChecking;
          } else if (p.phase === 'uploading') {
            const mine = clamp(loaded - offset, 0, size);
            if (mine >= size) text = s.statusSent;
            else if (mine > 0) text = fill(s.statusUploading, { percent: Math.floor(mine / size * 100) });
            else text = s.statusWaiting;
          } else {
            text = s.statusWaiting;
          }
        }
        offset += size;
        text = text || '';
        if (row.status.textContent !== text) row.status.textContent = text;
        row.status.hidden = !text;
        row.li.classList.toggle('is-done', state === 'done');
        row.li.classList.toggle('is-failed', state === 'failed');
      });
    }

    renderProgress() {
      const els = this.els;
      const s = this.strings;
      const p = this.progress;
      if (this.phase === 'uploading' || this.phase === 'sending') {
        const pct = p ? clamp((Number(p.fraction) || 0) * 100, 0, 100) : 0;
        const shown = Math.floor(pct);
        if (this.phase === 'uploading') this.setButtonText(fill(s.uploading, { percent: shown }));
        if (els.fill) els.fill.style.width = pct + '%';
        if (els.bar) els.bar.setAttribute('aria-valuenow', String(shown));
        if (els.detail && p) {
          const values = {
            loaded: formatBytes(Number(p.loaded) || 0),
            total: formatBytes(Number(p.total) || this.totalBytes()),
            percent: shown,
            files_done: Number(p.filesDone) || 0,
            files_total: Number(p.filesTotal) || this.files.length
          };
          let detail;
          if (this.phase === 'sending') {
            detail = s.received || s.finishing;
          } else if (p.phase === 'completing' || p.phase === 'done') {
            detail = s.finishing;
          } else if (p.bytesPerSecond > 0 && typeof p.etaSeconds === 'number' && isFinite(p.etaSeconds)) {
            values.speed = formatBytes(p.bytesPerSecond) + '/s';
            values.eta = formatDuration(p.etaSeconds);
            detail = fill(s.progressDetail, values);
          } else {
            detail = fill(s.progressStart, values);
          }
          detail = detail || '';
          if (els.detail.textContent !== detail) els.detail.textContent = detail;
        }
        this.renderStatuses();
      } else {
        if (els.fill) els.fill.style.width = '0%';
        if (els.bar) els.bar.setAttribute('aria-valuenow', '0');
        if (els.detail) els.detail.textContent = '';
      }
    }

    /* ------------------------------------------------------------- test */

    getState() {
      const f = this.els;
      const field = function (el) { return el ? { value: el.value, disabled: el.disabled } : null; };
      return {
        phase: this.phase,
        busy: this.busy(),
        files: this.files.map((entry) => {
          const row = this.rows.get(entry.id);
          return { name: entry.file.name, size: entry.file.size, status: row ? row.status.textContent : '' };
        }),
        totalBytes: this.totalBytes(),
        messages: this.messages.slice(),
        error: this.error ? this.error.message : '',
        code: this.error ? this.error.code : '',
        uploaded: !!this.cached,
        sendHeld: this.sendHeld,
        progress: this.progress ? Object.assign({}, this.progress) : null,
        button: f.submit ? { text: f.submit.textContent, disabled: f.submit.disabled, hidden: f.submit.hidden } : null,
        fields: { files: field(f.filesField), links: field(f.linksField), reference: field(f.referenceField) },
        lastSubmission: this.lastSubmission,
        uploadBusy: !!(window.PrinsteyUpload && window.PrinsteyUpload.busy)
      };
    }
  }

  /* ------------------------------------------------------------------ boot */

  function init(scope) {
    const base = scope && scope.querySelectorAll ? scope : document;
    const roots = [];
    if (base.matches && base.matches('[data-ccf-uploads]')) roots.push(base);
    Array.prototype.push.apply(roots, base.querySelectorAll('[data-ccf-uploads]'));
    roots.forEach(function (root) {
      if (root.__ccfUpload || !root.querySelector('form [data-ccf-attach]')) return;
      const instance = new ContactUpload(root);
      root.__ccfUpload = instance;
      instances.push(instance);
    });
  }

  function destroy(scope) {
    instances.slice().forEach(function (instance) {
      if (!scope || scope === instance.root || (scope.contains && scope.contains(instance.root))) instance.destroy();
    });
  }

  window.PrinsteyContact = { version: VERSION, instances: instances, init: init, destroy: destroy };

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { destroy(e.target); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
